'use client';

import { useToast } from '@/components/ui/Toasts/use-toast';
import { Button } from '@/components/ui/button';
import { useBookmark } from '@/lib/hooks/useBookmark';

import { Bookmark, BookmarkCheck } from 'lucide-react';

interface BookmarkButtonProps {
  articleSlug: string;
  size?: 'sm' | 'lg';
  variant?: 'default' | 'outline' | 'ghost';
}

export function BookmarkButton({
  articleSlug,
  size = 'sm',
  variant = 'ghost'
}: BookmarkButtonProps) {
  const { toast } = useToast();
  const { isBookmarked, isLoading, toggleBookmark } = useBookmark(articleSlug);

  const handleBookmark = async () => {
    try {
      const wasBookmarked = await toggleBookmark();
      if (wasBookmarked) {
        toast.success('Article bookmarked', {
          description: 'You can find this in your reading history.'
        });
      } else {
        toast.info('Bookmark removed');
      }
    } catch (error) {
      toast.error('Failed to update bookmark');
    }
  };

  return (
    <div className='relative'>
      <Button
        variant={variant}
        size={size}
        onClick={handleBookmark}
        disabled={isLoading}
        className='relative'>
        {isBookmarked ? (
          <BookmarkCheck className='size-4' />
        ) : (
          <Bookmark className='size-4' />
        )}
        <span className='sr-only'>
          {isBookmarked ? 'Remove bookmark' : 'Bookmark article'}
        </span>
      </Button>
    </div>
  );
}
